// SPDX-License-Identifier: AGPL-3.0-or-later

import type {VoiceEngineApi, VoiceEngineEvent} from '@app/features/platform/types/Electron';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getNativeVoiceEngineConnectionEventAction} from '@app/features/voice/engine/native_voice_engine/nativeVoiceEngineEventMapper';
import type {
	VoiceEngine,
	VoiceEngineAudioInputDevice,
	VoiceEngineAudioOutputDevice,
	VoiceEngineCameraDevice,
	VoiceEngineConnectParams,
	VoiceEngineEventListener,
	VoiceEnginePublishCameraParams,
	VoiceEnginePublishMicrophoneParams,
	VoiceEnginePublishScreenParams,
	VoiceEngineStats,
} from '@app/features/voice/engine/native_voice_engine/VoiceEngine';

const logger = new Logger('NativeVoiceEngine');

function encodeE2EEKey(key: string | null): ArrayBuffer | null {
	if (!key) return null;
	const encoded = new TextEncoder().encode(key);
	return encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength);
}

export function floatToPcm16Buffer(channelData: Float32Array): ArrayBuffer {
	const buffer = new ArrayBuffer(channelData.length * 2);
	const view = new DataView(buffer);
	for (let i = 0; i < channelData.length; i++) {
		const clamped = Math.max(-1, Math.min(1, channelData[i] ?? 0));
		view.setInt16(i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
	}
	return buffer;
}

function toOwnedArrayBuffer(payload: ArrayBuffer | Uint8Array): ArrayBuffer {
	if (payload instanceof ArrayBuffer) return payload.slice(0);
	const copy = new Uint8Array(payload.byteLength);
	copy.set(payload);
	return copy.buffer;
}

export class NativeVoiceEngine implements VoiceEngine {
	readonly kind = 'native' as const;
	private readonly bridge: VoiceEngineApi;
	private connected = false;
	private microphonePublished = false;
	private microphoneConfigKey: string | null = null;

	constructor(bridge: VoiceEngineApi) {
		this.bridge = bridge;
	}

	async connect(params: VoiceEngineConnectParams): Promise<void> {
		const e2eeKey = encodeE2EEKey(params.e2eeKey);
		await this.bridge.connect({url: params.url, token: params.token, e2eeKey});
		this.connected = true;
		logger.info('Native voice engine connect requested', {hasE2EE: e2eeKey != null});
	}

	async disconnect(): Promise<void> {
		this.connected = false;
		this.microphonePublished = false;
		this.microphoneConfigKey = null;
		await this.bridge.disconnect();
	}

	isConnected(): boolean {
		return this.connected;
	}

	async publishMicrophone(params: VoiceEnginePublishMicrophoneParams = {}): Promise<void> {
		const requestedDeviceId = params.deviceId?.trim() || null;
		const publishOptions = {
			deviceId: requestedDeviceId ?? undefined,
			echoCancellation: params.echoCancellation,
			noiseSuppression: params.noiseSuppression,
			autoGainControl: params.autoGainControl,
		};
		const configKey = JSON.stringify(publishOptions);
		if (this.microphonePublished && this.microphoneConfigKey === configKey) return;
		await this.bridge.publishMicrophone(publishOptions);
		this.microphonePublished = true;
		this.microphoneConfigKey = configKey;
		logger.info('Native voice engine microphone publish requested', {
			hasDeviceId: requestedDeviceId != null,
		});
	}

	async publishScreenShare(params: VoiceEnginePublishScreenParams): Promise<void> {
		await this.bridge.publishScreen({
			captureId: params.captureId,
			width: params.width,
			height: params.height,
			codec: params.codec,
			maxBitrateBps: params.maxBitrateBps,
			maxFramerate: params.maxFramerate,
		});
		logger.info('Native voice engine screen-share publish requested', {captureId: params.captureId});
	}

	async unpublishScreenShare(): Promise<void> {
		await this.bridge.unpublishScreen();
	}

	async setMicEnabled(enabled: boolean): Promise<void> {
		if (enabled && !this.microphonePublished) {
			await this.publishMicrophone();
		}
		await this.bridge.setMicEnabled(enabled);
	}

	async publishCamera(params: VoiceEnginePublishCameraParams): Promise<void> {
		await this.bridge.publishCamera({
			deviceId: params.deviceId,
			width: params.width,
			height: params.height,
			frameRate: params.frameRate,
		});
		logger.info('Native voice engine camera publish requested', {hasDeviceId: params.deviceId != null});
	}

	async listCameraDevices(): Promise<Array<VoiceEngineCameraDevice>> {
		return this.bridge.listCameraDevices();
	}

	async unpublishCamera(): Promise<void> {
		await this.bridge.unpublishCamera();
	}

	async listAudioInputDevices(): Promise<Array<VoiceEngineAudioInputDevice>> {
		return this.bridge.listAudioInputDevices();
	}

	async listAudioOutputDevices(): Promise<Array<VoiceEngineAudioOutputDevice>> {
		return this.bridge.listAudioOutputDevices();
	}

	async setAudioOutputDevice(deviceId: string): Promise<void> {
		await this.bridge.setAudioOutputDevice(deviceId);
	}

	async setParticipantVolume(participantSid: string, volume: number): Promise<void> {
		const clamped = Math.max(0, Math.min(2, volume));
		await this.bridge.setParticipantVolume(participantSid, clamped);
	}

	async setRemoteTrackSubscription(params: Parameters<VoiceEngine['setRemoteTrackSubscription']>[0]): Promise<void> {
		await this.bridge.setRemoteTrackSubscription(params);
	}

	async publishData(params: Parameters<VoiceEngine['publishData']>[0]): Promise<void> {
		await this.bridge.publishData({
			payload: toOwnedArrayBuffer(params.payload),
			reliable: params.reliable,
			topic: params.topic,
			destinationIdentities: params.destinationIdentities,
		});
	}

	async getConnectionStats(): Promise<VoiceEngineStats | null> {
		return this.bridge.getConnectionStats();
	}

	onEvent(listener: VoiceEngineEventListener): () => void {
		return this.bridge.onEvent((event: VoiceEngineEvent) => {
			const connectionAction = getNativeVoiceEngineConnectionEventAction(event);
			if (connectionAction === 'connected' || connectionAction === 'reconnected') {
				this.connected = true;
			} else if (connectionAction === 'disconnected') {
				this.connected = false;
			}
			try {
				listener(event);
			} catch (error) {
				logger.error('Native voice engine event listener threw', {type: event.type, error});
			}
		});
	}
}
